import { defineStore } from 'pinia'
import type { ServiceRunRequest } from '~/types/report-service'

export interface ReportDownloadsStore {
  isOpen: Ref<boolean>
  activeCount: Ref<number>
  pendingRequests: Ref<Record<string, ServiceRunRequest>>
  lastSeenDoneIds: Ref<string[] | null>
  setActiveCount: (count: number) => void
  open: () => void
  close: () => void
  toggle: () => void
  rememberRequest: (runId: string, request: ServiceRunRequest) => void
  forgetRequest: (runId: string) => void
  requestFor: (runId: string) => ServiceRunRequest | undefined
  markTerminal: (ids: string[]) => string[]
  $reset: () => void
}

export const useReportDownloadsStore = defineStore('reportDownloads', (): ReportDownloadsStore => {
  const isOpen = ref(false)
  const activeCount = ref(0)
  const pendingRequests = ref<Record<string, ServiceRunRequest>>({})
  const lastSeenDoneIds = ref<string[] | null>(null)

  const setActiveCount = (count: number): void => {
    activeCount.value = Number.isFinite(count) && count > 0 ? Math.trunc(count) : 0
  }

  const open = (): void => {
    isOpen.value = true
  }

  const close = (): void => {
    isOpen.value = false
  }

  const toggle = (): void => {
    isOpen.value = !isOpen.value
  }

  const rememberRequest = (runId: string, request: ServiceRunRequest): void => {
    pendingRequests.value = { ...pendingRequests.value, [runId]: request }
  }

  const forgetRequest = (runId: string): void => {
    const { [runId]: _removed, ...rest } = pendingRequests.value
    pendingRequests.value = rest
  }

  const requestFor = (runId: string): ServiceRunRequest | undefined => pendingRequests.value[runId]

  const markTerminal = (ids: string[]): string[] => {
    const previous = lastSeenDoneIds.value
    lastSeenDoneIds.value = [...ids]
    if (previous === null) return []
    return ids.filter((id) => !previous.includes(id))
  }

  const $reset = (): void => {
    isOpen.value = false
    activeCount.value = 0
    pendingRequests.value = {}
    lastSeenDoneIds.value = null
  }

  return {
    isOpen,
    activeCount,
    pendingRequests,
    lastSeenDoneIds,
    setActiveCount,
    open,
    close,
    toggle,
    rememberRequest,
    forgetRequest,
    requestFor,
    markTerminal,
    $reset
  }
})
